import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  TextField,
  Button,
} from "@mui/material";
import { useSnackbar } from "notistack";

/**
 * Validates that both API Key and Security Key are non-empty.
 * @param {string} apiKey
 * @param {string} securityKey
 * @returns {Array<string>} List of validation error messages.
 */
export const validateApiCredentials = (apiKey, securityKey) => {
  const errors = [];
  if (!apiKey || String(apiKey).trim() === "") {
    errors.push("Please Enter Valid API Key");
  } else if (!securityKey || String(securityKey).trim() === "") {
    errors.push("Please Enter Valid Security Key");
  }
  return errors;
};

/**
 * Dialog component for configuring vendor API credentials (API Key & Security Key).
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open.
 * @param {Function} props.onClose - Modal close handler.
 * @param {Object} props.initialData - Current credentials { apiKey, securityKey }.
 * @param {Function} props.onSave - Callback when credentials are successfully validated and saved.
 */
const ApiConfigurationModal = ({ open, onClose, initialData, onSave }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [credentials, setCredentials] = useState({
    apiKey: "",
    securityKey: "",
  });

  // Sync internal state with props when modal opens
  useEffect(() => {
    if (open) {
      setCredentials({
        apiKey: initialData?.apiKey || "",
        securityKey: initialData?.securityKey || "",
      });
    }
  }, [open, initialData]);

  const handleChange = (field, value) => {
    setCredentials((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = useCallback(() => {
    const errors = validateApiCredentials(credentials.apiKey, credentials.securityKey);
    if (errors.length > 0) {
      errors.forEach((err) => enqueueSnackbar(err, { variant: "error" }));
      return;
    }

    onSave({
      apiKey: credentials.apiKey.trim(),
      securityKey: credentials.securityKey.trim(),
    });
    onClose();
  }, [credentials, enqueueSnackbar, onSave, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "6px" } }}
    >
      <DialogTitle
        sx={{
          fontSize: "0.8rem",
          fontWeight: 600,
          color: "#2782EC",
          bgcolor: "#F8F9FA",
          py: 1,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        API Configuration
      </DialogTitle>
      <DialogContent sx={{ mt: 2, p: 3 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography fontSize="0.7rem" fontWeight={500} sx={{ minWidth: 90 }}>
              API KEY <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={credentials.apiKey}
              onChange={(e) => handleChange("apiKey", e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { height: "28px", fontSize: "0.7rem" } }}
              inputProps={{ "aria-label": "API Key" }}
            />
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography fontSize="0.7rem" fontWeight={500} sx={{ minWidth: 90 }}>
              Security KEY <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={credentials.securityKey}
              onChange={(e) => handleChange("securityKey", e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { height: "28px", fontSize: "0.7rem" } }}
              inputProps={{ "aria-label": "Security Key" }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          size="small"
          variant="contained"
          onClick={onClose}
          sx={{
            backgroundColor: "#EFDBE3",
            color: "#d32f2f",
            textTransform: "none",
            boxShadow: "none",
            fontSize: "0.7rem",
            "&:hover": { backgroundColor: "#f48fb1", boxShadow: "none" },
          }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSave}
          sx={{
            backgroundColor: "#b5dcf2",
            color: "#000",
            textTransform: "none",
            boxShadow: "none",
            fontSize: "0.7rem",
            "&:hover": { backgroundColor: "#a7d2ea", boxShadow: "none" },
          }}
        >
          Validate & Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApiConfigurationModal;
